/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { map, Observable } from 'rxjs';
import { DialogService } from 'nebula-ui-kit';
import { inject, Injectable } from '@angular/core';
import { SelectDialogComponent } from '../../../select-dialog/select-dialog.component';
import { CostCenterService } from './centro-costos.service';

export interface CentroCostoSelect {
  id: number;
  codigo: string;
  nombre: string;
  idTipoCentroCostos?: number;
  nombreTipoCentroCostos?: string;
  status: string;
}

export interface CentroCostoFilters {
  tipoCentroCostoId?: number;
  status?: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
}

@Injectable({ providedIn: 'root' })
export class CentroCostoModalService {
  readonly dialogService = inject(DialogService);
  readonly costCenterService = inject(CostCenterService);

  openSelector(
    title: string,
    currentValue?: string,
    filters?: CentroCostoFilters,
    preloadedCentros?: CentroCostoSelect[],
  ): Observable<CentroCostoSelect | null> {
    const loadDataFn = this.createLoadDataFn(filters, preloadedCentros);

    const dialogRef = this.dialogService.open(SelectDialogComponent, {
      data: {
        title: title,
        columns: this.getColumns(),
        multiSelect: false,
        searchPlaceholder: 'Buscar por código o nombre...',
        loadDataFn: loadDataFn,
        itemIdKey: 'id',
        displayKey: 'codigo',
        selectedItems: currentValue ? [{ codigo: currentValue, nombre: currentValue }] : [],
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100],
      },
      size: 'lg',
      width: '800px',
    });

    return dialogRef
      .afterClosed()
      .pipe(map((selected: CentroCostoSelect[]) => selected?.[0] || null));
  }

  private getColumns() {
    return [
      { key: 'codigo', label: 'Código', sortable: true, width: '30%' },
      { key: 'nombre', label: 'Nombre', sortable: true, width: '50%' },
      { key: 'nombreTipoCentroCostos', label: 'Tipo Centro Costo', sortable: true, width: '20%' },
    ];
  }

  private createLoadDataFn(filters?: CentroCostoFilters, preloadedCentros?: CentroCostoSelect[]) {
    return async (params: any): Promise<{ data: CentroCostoSelect[]; total: number }> => {
      const page = params.page - 1;
      const size = params.limit || 10;
      const searchTerm = params.search || '';

      try {
        let rawCentros: any[] = [];

        if (preloadedCentros && preloadedCentros.length > 0) {
          rawCentros = preloadedCentros;
        } else if (filters?.tipoCentroCostoId) {
          rawCentros =
            (await this.costCenterService
              .getCentrosByTipo(filters.tipoCentroCostoId)
              .toPromise()) || [];
        } else {
          rawCentros = (await this.costCenterService.getActiveCentros().toPromise()) || [];
        }

        const centros: CentroCostoSelect[] = rawCentros.map((c: any) => ({
          id: c.id,
          codigo: c.codigo,
          nombre: c.nombre,
          idTipoCentroCostos: c.idTipoCentroCostos ?? c.tipoCentroCostoId,
          nombreTipoCentroCostos: c.nombreTipoCentroCostos ?? c.tipoCentroCostoNombre,
          status:
            c.status === 'ACTIVE' || c.estado === 'Activo'
              ? 'ACTIVE'
              : c.status === 'INACTIVE' || c.estado === 'Inactivo'
                ? 'INACTIVE'
                : c.status === 'BLOCKED' || c.estado === 'Bloqueado'
                  ? 'BLOCKED'
                  : 'ACTIVE',
        }));

        let filteredCentros = centros;

        if (filters?.status && !preloadedCentros) {
          filteredCentros = filteredCentros.filter((c) => c.status === filters.status);
        }

        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          filteredCentros = filteredCentros.filter(
            (c) => c.codigo?.toLowerCase().includes(term) || c.nombre?.toLowerCase().includes(term),
          );
        }

        const start = page * size;
        const end = start + size;
        const paginatedData = filteredCentros.slice(start, end);

        return {
          data: paginatedData,
          total: filteredCentros.length,
        };
      } catch (error) {
        return { data: [], total: 0 };
      }
    };
  }
}
